import {
  type ReactNode,
  type TextareaHTMLAttributes,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
} from "react";
import { cx } from "../cx";
import { controlClass, Field, fieldState } from "./Field";

/**
 * Multi-line input. `autosize` grows with the content (the Block editor);
 * `serif` sets the book text face so an edited Block looks like the script.
 */
export function TextArea({
  label,
  hint,
  error,
  id: givenId,
  className = "",
  autosize = false,
  serif = false,
  rows = 3,
  onChange,
  value,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  autosize?: boolean;
  serif?: boolean;
}) {
  const generated = useId();
  const id = givenId ?? generated;
  const { invalid, describedBy } = fieldState(id, error, hint);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el || !autosize) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autosize]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refit whenever the controlled value changes
  useLayoutEffect(fit, [fit, value]);
  return (
    <Field
      id={id}
      className={className}
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(invalid ? { error } : {})}
    >
      <textarea
        {...rest}
        ref={ref}
        id={id}
        rows={rows}
        value={value}
        onChange={(e) => {
          onChange?.(e);
          fit();
        }}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-component="TextArea"
        className={controlClass(
          invalid,
          cx("py-2", autosize && "resize-none overflow-hidden", serif && "font-serif"),
          serif ? "text-book" : "text-ui",
        )}
      />
    </Field>
  );
}
